'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var _slicedToArray = require('@babel/runtime/helpers/slicedToArray');
var React = require('react');
var PropTypes = require('prop-types');
var reactChartjs2 = require('react-chartjs-2');
var designTokenUtils = require('./designTokenUtils-bdd52fe1.js');
var Typography = require('./Typography-d8cec00c.js');
var react = require('@linaria/react');
var jsxRuntime = require('react/jsx-runtime');
require('@babel/runtime/helpers/defineProperty');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

function _interopNamespace(e) {
  if (e && e.__esModule) return e;
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () { return e[k]; }
        });
      }
    });
  }
  n["default"] = e;
  return Object.freeze(n);
}

var _slicedToArray__default = /*#__PURE__*/_interopDefaultLegacy(_slicedToArray);
var PropTypes__default = /*#__PURE__*/_interopDefaultLegacy(PropTypes);

var options = {
  cutout: '85%',
  rotation: 225,
  circumference: 270,
  events: []
};

var StyledCreditScore = /*#__PURE__*/react.styled('div')({
  name: "StyledCreditScore",
  "class": "s1vx0siu",
  propsAsIs: false
});

var chartJS = Promise.resolve().then(function () { return /*#__PURE__*/_interopNamespace(require('chart.js')); });
var Sizes = ['large', 'medium', 'small'];
var propTypes = {
  /** Credit score to show in the chart. Minimum of 300 and maximum of 850 */
  creditScore: PropTypes__default["default"].number.isRequired,
  /** Date that the score was fetched. Defaults to today */
  captureDate: PropTypes__default["default"].string,
  /** Size of the credit score chart: 'large', 'medium', 'small'. Defaults to small */
  size: PropTypes__default["default"].oneOf(Sizes),
  /** Styling classes used on the wrapper of the component */
  className: PropTypes__default["default"].string
};
/**
 * @param creditScore Credit score to show in the chart. Minimum of 300 and maximum of 850
 * @param captureDate Date that the score was fetched. Defaults to today
 * @param size Size of the credit score chart: 'large', 'medium', 'small'. Defaults to small
 * @param className Styling classes used on the wrapper of the component
 */

// Resources:
// https://www.chartjs.org/docs/latest/charts/doughnut.html
// https://react-chartjs-2.js.org/components/doughnut
var CreditScore = function CreditScore(_ref) {
  var creditScore = _ref.creditScore,
    captureDate = _ref.captureDate,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'small' : _ref$size,
    className = _ref.className;
  var _useState = React.useState(false),
    _useState2 = _slicedToArray__default["default"](_useState, 2),
    chartImported = _useState2[0],
    setChartImported = _useState2[1];
  var score = Math.max(300, Math.min(850, creditScore));
  var COLORS = {
    Excellent: designTokenUtils.getTokenValue('--color-credit-excellent').length > 0 ? designTokenUtils.getTokenValue('--color-credit-excellent') : '#54BA83',
    Fair: designTokenUtils.getTokenValue('--color-credit-fair').length > 0 ? designTokenUtils.getTokenValue('--color-credit-fair') : '#FFBE16',
    Good: designTokenUtils.getTokenValue('--color-credit-good').length > 0 ? designTokenUtils.getTokenValue('--color-credit-good') : '#2175DA',
    Poor: designTokenUtils.getTokenValue('--color-credit-poor').length > 0 ? designTokenUtils.getTokenValue('--color-credit-poor') : '#C12950'
  };
  var BASE_COLOR = designTokenUtils.getTokenValue('--color-secondary-inactive-lightest').length > 0 ? designTokenUtils.getTokenValue('--color-secondary-inactive-lightest') : '#F3F3F3';
  var getLabelByCredit = function getLabelByCredit(score) {
    if (score >= 740) {
      return 'Excellent';
    }
    if (score >= 660) {
      return 'Good';
    }
    if (score >= 580) {
      return 'Fair';
    }
    return 'Poor';
  };
  var label = getLabelByCredit(score);
  var dataColors = [COLORS[label], BASE_COLOR];
  var date = captureDate ? new Date(captureDate) : new Date();
  React.useEffect(function () {
    chartJS.then(function (mod) {
      var Chart = mod.Chart;
      var CategoryScale = mod.CategoryScale;
      var LinearScale = mod.LinearScale;
      var PointElement = mod.PointElement;
      var LineElement = mod.LineElement;
      var ArcElement = mod.ArcElement;
      var Title = mod.Title;
      var Tooltip = mod.Tooltip;
      var Legend = mod.Legend;
      Chart.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);
      setChartImported(true);
    });
  }, []);

  /**
   * Used to draw the overlapping section of the donut chart.
   * Reference video from chartJs: https://www.youtube.com/watch?v=jWXQ7lsg-Yw
   */
  var afterDatasetsDraw = function afterDatasetsDraw(chart, args, plugins) {
    var ctx = chart.ctx;
      chart.data;
    var x = chart.getDatasetMeta(0).data[0].x;
    var y = chart.getDatasetMeta(0).data[0].y;
    var angle = Math.PI / 180;
    var endAngle = chart.getDatasetMeta(0).data[0].endAngle;
    var innerRadius = chart.getDatasetMeta(0).data[0].innerRadius;
    var outerRadius = chart.getDatasetMeta(0).data[0].outerRadius;
    var radius = (outerRadius - innerRadius) / 2;
    var coordinates = [];
    // Coordinates to draw many circles around a point to round the edge between the two sections
    for (var i = -0.04; i <= 0.04; i += 0.005) {
      var xCoor = (innerRadius + radius) * Math.cos(endAngle + Math.PI + i);
      var yCoor = (innerRadius + radius) * Math.sin(endAngle + i);
      coordinates.push({
        x: xCoor,
        y: yCoor
      });
    }
    ctx.save();
    ctx.fillStyle = dataColors[0];
    ctx.translate(x, y);
    ctx.beginPath();

    // Draw the circles at the border of the two sections to round the connection
    coordinates.forEach(function (_ref2) {
      var x = _ref2.x,
        y = _ref2.y;
      ctx.arc(-x, y, radius + 1, 0, angle * 360, false);
    });
    ctx.fill();
    ctx.restore();
    chart.update();
  };

  /**
   * Used to draw the credit score inside of the chart
   * Reference video from chartJs: https://www.youtube.com/watch?v=4LFXFDgMBuc&t=3s
   */
  var beforeDatasetDraw = function beforeDatasetDraw(chart, args, plugins) {
    var ctx = chart.ctx,
      data = chart.data;
    var x = chart.getDatasetMeta(0).data[0].x;
    var y = chart.getDatasetMeta(0).data[0].y;
    var FONT_SIZE = size === 'small' ? 3.5 : size === 'medium' ? 4.5 : 6;
    ctx.save();
    ctx.font = "bold ".concat(FONT_SIZE, "rem Proxima Nova, sans-serif");
    ctx.fillStyle = 'black';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(data.datasets[0].data[0], x, y - (FONT_SIZE - 3.5) * 8);
    ctx.restore();
    chart.update();
  };
  return /*#__PURE__*/jsxRuntime.jsx(StyledCreditScore, {
    className: className || '',
    children: /*#__PURE__*/jsxRuntime.jsxs("div", {
      className: "credit-score ".concat(size),
      children: [/*#__PURE__*/jsxRuntime.jsx("div", {
        className: "flex-container",
        id: "chart-container",
        children: /*#__PURE__*/jsxRuntime.jsx("div", {
          className: "chart",
          "data-chromatic": "ignore",
          children: chartImported && /*#__PURE__*/jsxRuntime.jsx(reactChartjs2.Doughnut, {
            redraw: true,
            options: options,
            data: {
              datasets: [{
                data: [score, 1000 - score],
                backgroundColor: dataColors,
                borderColor: dataColors,
                borderWidth: 2,
                borderRadius: 99,
                borderJoinStyle: 'miter'
              }]
            },
            plugins: [{
              id: 'afterDataSetsDraw',
              afterDatasetsDraw: afterDatasetsDraw
            }, {
              id: 'beforeDatasetDraw',
              beforeDatasetDraw: beforeDatasetDraw
            }]
          })
        })
      }), /*#__PURE__*/jsxRuntime.jsx("div", {
        className: "label-container",
        children: /*#__PURE__*/jsxRuntime.jsx("span", {
          className: "label",
          children: label
        })
      }), /*#__PURE__*/jsxRuntime.jsx("div", {
        className: "date-container",
        "data-chromatic": "ignore",
        children: /*#__PURE__*/jsxRuntime.jsxs(Typography.Typography, {
          variant: size === 'large' ? 'bodyL' : 'bodyM',
          children: ["Captured ", /*#__PURE__*/jsxRuntime.jsx("strong", {
            children: date.toLocaleDateString()
          })]
        })
      })]
    })
  });
};
CreditScore.propTypes = propTypes;

exports["default"] = CreditScore;
